// How an unsaved change is drawn (TGCLOUD-1902), from the theme's
// palette.changed (src/theme/palette.js). The shapes are the data entry grid's
// and the Test modal's, which this replaces (TGCLOUD-1866): a gold ground and
// outline on an input, a gold outline round a control with no outline of its own,
// a gold bar down a table row, a gold card for a tile. A field has no bar beside
// it (Tim, 2026-10-13): its "Changed" label is the cue that is not a colour
// (rule 3). Nothing here moves the field, so an input never moves under the
// clinician's typing as it turns gold.

import { changedPalette } from 'src/theme/palette';

export const CHANGED_BAR_WIDTH = 3;

/** Read by screen readers, not shown. Strings: in `sx` a bare 1 means 100%. */
export const VISUALLY_HIDDEN = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px'
};

// The outlined inputs a field holds, and each one's outline: MUI's own, and
// MUI X's date pickers'.
const OUTLINED_INPUTS = [
  ['MuiOutlinedInput-root', 'MuiOutlinedInput-notchedOutline'],
  ['MuiPickersOutlinedInput-root', 'MuiPickersOutlinedInput-notchedOutline']
];

/**
 * The ground and outline of every input a field holds. The focus outline stays
 * the theme's while the input is focused, and the ERROR outline wins over the
 * gold (rule 8); a disabled input is never drawn.
 */
export const changedInputsSx = (palette) => {
  const sx = {};
  for (const [root, outline] of OUTLINED_INPUTS) {
    sx[`& .${root}:not(.Mui-disabled)`] = { backgroundColor: palette.bg };
    sx[
      `& .${root}:not(.Mui-error):not(.Mui-focused):not(.Mui-disabled) .${outline}`
    ] = { borderColor: palette.main };
  }
  // A filled or standard input has no outline: the ground is its cue.
  sx[
    '& .MuiFilledInput-root:not(.Mui-disabled), & .MuiInput-root:not(.Mui-disabled)'
  ] = { backgroundColor: palette.bg };
  return sx;
};

/**
 * The "Changed" label, at the top right of the field. No ground of its own
 * (Tim, 2026-10-13): it sits on whatever the field sits on, the sidebar's grey
 * included, where `palette.text` still reads at 4.5:1.
 */
export const changedLabelSx = (palette, placement = 'top-end') => ({
  position: 'absolute',
  ...(placement === 'bottom-end' ? { bottom: 2 } : { top: 0 }),
  right: placement === 'bottom-end' ? 4 : 0,
  zIndex: 1,
  color: palette.text,
  fontSize: '0.68rem',
  fontWeight: 600,
  lineHeight: '1rem',
  letterSpacing: 0.2,
  whiteSpace: 'nowrap',
  pointerEvents: 'none'
});

/**
 * sx for a chip standing for a record added and not saved yet (Bulk Edit's new
 * diseases, phenotypes and genotypes): the gold ground and outline, and the bar
 * at its left edge, so the colour is not the only cue. Its text is
 * `palette.text`, which reads at 4.5:1 on the ground.
 */
export const changedChipSx = (palette) => ({
  bgcolor: palette.bg,
  color: palette.text,
  border: `1px solid ${palette.main}`,
  borderLeft: `${CHANGED_BAR_WIDTH}px solid ${palette.main}`,
  '& .MuiChip-deleteIcon': { color: palette.text }
});

/**
 * A record tile added and not saved yet (the sidebar's disease, phenotype and
 * genotype lists): plain CSS, for a `style` or an `sx`, spread after the tile's
 * own colours. The gold ground and outline, the bar, and the text shade.
 */
export const changedTileStyle = (palette) => ({
  backgroundColor: palette.bg,
  color: palette.text,
  border: `1px solid ${palette.main}`,
  borderLeft: `${CHANGED_BAR_WIDTH}px solid ${palette.main}`
});

/**
 * sx for the element that holds a changed field.
 *
 * @param {object} theme  the MUI theme
 * @param {object} state
 * @param {boolean} state.active  drawn as changed
 * @param {'field'|'control'|'row'|'tile'|'cell'} [state.variant]
 *   field    holds outlined inputs (text, select, autocomplete, date)
 *   control  holds a control with no outline (switch, checkbox, radio group)
 *   row      is a table row
 *   tile     is a card
 *   cell     is the first cell of a row drawn with `row`: it carries the
 *            tooltip and the description (a <tr> may hold only cells), and
 *            draws nothing itself
 */
export function changedSx(theme, { active, variant = 'field' }) {
  const palette = changedPalette(theme);
  const base = variant === 'row' ? {} : { position: 'relative' };
  if (!active) return base;
  switch (variant) {
    case 'cell':
      return base;
    case 'row':
      return {
        borderLeft: `${CHANGED_BAR_WIDTH}px solid ${palette.main}`,
        '& > td, & > th': { backgroundColor: palette.bg }
      };
    case 'tile':
      return {
        ...base,
        border: `1px solid ${palette.main}`,
        borderLeft: `${CHANGED_BAR_WIDTH}px solid ${palette.main}`,
        backgroundColor: palette.bg
      };
    case 'control':
      return {
        ...base,
        backgroundColor: palette.bg,
        outline: `1px solid ${palette.main}`,
        borderRadius: 1
      };
    default:
      return { ...base, ...changedInputsSx(palette) };
  }
}
